import { formatDate } from '@angular/common';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import {
    LETZTE_AKTIONEN_ZEITRAUM_DEFAULT,
    LetzteAktionenZeitraum,
} from '../../shared/letzte-aktionen-zeitraum';

export type { LetzteAktionenZeitraum };
export { LETZTE_AKTIONEN_ZEITRAUM_DEFAULT };

/** Browser cache (instant paint). Source of truth across devices = speicher. */
const LETZTE_AKTIONEN_ZEITRAUM_STORAGE_KEY = 'letzte_aktionen_zeitraum_v1';

/**
 * Shared user pref (desktop + mobile) via `speicher/read_value|update`.
 * Value shape: `{ zeitraum: '30d'|'6m'|'all', ...optional mobile fields }`.
 * Legacy: plain string `'30d'|'6m'|'all'` also accepted when reading.
 * Removed `'7d'` (1 Woche) falls back to the default (1 Monat).
 */
export const LETZTE_AKTIONEN_FILTER_SPEICHER_KEY = 'letzte_aktionen_filter_v1';

export const LETZTE_AKTIONEN_ZEITRAUM_OPTIONS: ReadonlyArray<{
    value: LetzteAktionenZeitraum;
    label: string;
}> = [
    { value: '30d', label: '1 Monat' },
    { value: '6m', label: '6 Monate' },
    { value: 'all', label: 'Gesamt' },
];

function isLetzteAktionenZeitraum(value: unknown): value is LetzteAktionenZeitraum {
    return value === '30d' || value === '6m' || value === 'all';
}

export function parseLetzteAktionenZeitraum(raw: unknown): LetzteAktionenZeitraum | null {
    if (isLetzteAktionenZeitraum(raw)) {
        return raw;
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const z = (raw as { zeitraum?: unknown }).zeitraum;
        if (isLetzteAktionenZeitraum(z)) {
            return z;
        }
    }
    return null;
}

/** Local cache only — hydrate from speicher after login for cross-device sync. */
export function loadLetzteAktionenZeitraum(): LetzteAktionenZeitraum {
    try {
        const raw = localStorage.getItem(LETZTE_AKTIONEN_ZEITRAUM_STORAGE_KEY);
        const parsed = parseLetzteAktionenZeitraum(raw);
        if (parsed) {
            return parsed;
        }
    } catch {
        // ignore
    }
    return LETZTE_AKTIONEN_ZEITRAUM_DEFAULT;
}

export function saveLetzteAktionenZeitraum(zeitraum: LetzteAktionenZeitraum): void {
    try {
        localStorage.setItem(LETZTE_AKTIONEN_ZEITRAUM_STORAGE_KEY, zeitraum);
    } catch {
        // ignore
    }
}

/** Merge zeitraum into existing speicher blob so mobile-only fields survive. */
export function mergeLetzteAktionenFilterSpeicher(
    existing: unknown,
    zeitraum: LetzteAktionenZeitraum
): Record<string, unknown> {
    const base =
        existing && typeof existing === 'object' && !Array.isArray(existing)
            ? { ...(existing as Record<string, unknown>) }
            : {};
    return { ...base, zeitraum };
}

export enum TierLetzteAktionTyp {
    einstallen = 'einstallen',
    ausstallen = 'ausstallen',
}

export interface LetzteAktionWertAnzahl {
    wert: number | null;
    anzahl: number | null;
}

export interface TierLetzteAktionGruppeLink {
    gruppe_id: number;
    bezeichnung: string;
}

export interface TierLetzteAktionRow {
    aktion_id: number | null;
    aktion: TierLetzteAktionTyp;
    betrieb: string;
    /** Single-group shortcut; null when {@link gruppen} has multiple entries */
    gruppe_id: number | null;
    gruppen: TierLetzteAktionGruppeLink[];
    anzahl_tiere: number;
    aktuelle_gruppe: string;
    abschnitt: string;
    status: string;
    status_id_anzeige: number | null;
    listenstatus: string;
    listenstatus_id: number | null;
    tierliste_id: number | null;
    /** Resolved Einstall-liste for imported rows (metrics API). */
    einstall_tierliste_id?: number | null;
    /** Import flag from tierliste: 1 = import allowed, 0 = import closed (Nicht importieren). */
    import_einstallen?: boolean | null;
    /** tierlisten.kommentar (z. B. automatisch (Schlachtung) / HIT Sync / HIT Abmeldung). */
    kommentar: string;
    herkunft: string;
    /** Viehhändler label from tierliste (Einstallen rows). */
    handler?: string | null;
    /** Tierliste transfer within the same Betrieb (internal move). */
    transfer_innerhalb_betrieb: boolean;
    /** Gespeichertes Kennzeichen tierlisten.umstallen. */
    umstallen?: boolean;
    bevorzugt_importieren: boolean;
    status_hinweis: string | null;
    datum_raw: string | null;
    datum: Date;
    /** Min Einstall- oder Ausstalldatum der Tiere (Y-m-d). */
    bewegungsdatum_von?: string | null;
    /** Max Einstall- oder Ausstalldatum der Tiere (Y-m-d). */
    bewegungsdatum_bis?: string | null;
    /** 1/0: Empfänger darf Bio/Preise/Gewichte für diesen Betrieb sehen. */
    darf_bio?: number | boolean | null;
    kunde_id?: number | null;
    einkaufspreis_avg: LetzteAktionWertAnzahl | null;
    verkaufspreis_avg: LetzteAktionWertAnzahl | null;
    erloesdifferenz_avg: LetzteAktionWertAnzahl | null;
    tageszunahmen_avg: LetzteAktionWertAnzahl | null;
    /** Sum of einstallgewicht on animals (direct einstall / tierliste). */
    einstallgewicht_avg: LetzteAktionWertAnzahl | null;
    /** Sum of abgangsgewicht on animals (tierliste Ausstallen). */
    abgangsgewicht_avg: LetzteAktionWertAnzahl | null;
    /** tierlisten.typ when resolved (e.g. direct scan einstall). */
    tierliste_typ?: string | null;
}

export interface LetzteAktionenApiResponse {
    records: TierLetzteAktionApiRow[];
    count: number;
    recordcount?: number;
}

export interface TierLetzteAktionApiRow {
    aktion_id?: number | string | null;
    aktion: string;
    betrieb: string;
    gruppe_id: number | string | null;
    gruppen?: TierLetzteAktionGruppeLink[] | string | null;
    anzahl_tiere: number;
    aktuelle_gruppe: string;
    abschnitt?: string | null;
    status?: string;
    status_id?: number | string | null;
    status_id_anzeige?: number | string | null;
    listenstatus: string;
    listenstatus_id: number | string | null;
    tierliste_id: number | string | null;
    tierliste_typ?: string | null;
    einstall_tierliste_id?: number | string | null;
    import_einstallen?: number | boolean | null;
    herkunft: string;
    handler?: string | null;
    kommentar?: string | null;
    transfer_innerhalb_betrieb?: number | boolean | null;
    umstallen?: number | boolean | null;
    bevorzugt_importieren?: number | boolean | null;
    status_hinweis?: string | null;
    datum: string;
    bewegungsdatum_von?: string | null;
    bewegungsdatum_bis?: string | null;
    kunde_id?: number | string | null;
    darf_bio?: number | boolean | string | null;
    einkaufspreis_avg?: LetzteAktionWertAnzahl | null;
    verkaufspreis_avg?: LetzteAktionWertAnzahl | null;
    erloesdifferenz_avg?: LetzteAktionWertAnzahl | null;
    tageszunahmen_avg?: LetzteAktionWertAnzahl | null;
    einstallgewicht_avg?: LetzteAktionWertAnzahl | null;
    abgangsgewicht_avg?: LetzteAktionWertAnzahl | null;
}

const TIER_LETZTE_AKTION_LABELS: Record<TierLetzteAktionTyp, string> = {
    [TierLetzteAktionTyp.einstallen]: 'Einstallen',
    [TierLetzteAktionTyp.ausstallen]: 'Ausstallen',
};

const VALID_AKTION_TYPES = new Set<string>(Object.values(TierLetzteAktionTyp));

function normalizeLetzteAktionOriginLabel(value?: string | null): string {
    const trimmed = (value || '').trim();
    return trimmed && trimmed !== '—' ? trimmed : '';
}

/** Herkunft/Zielbetrieb column: Einstallen shows Herkunft or Händler (Herkunft wins if both set). */
export function getLetzteAktionHerkunftZielDisplay(
    row: Pick<TierLetzteAktionRow, 'aktion' | 'herkunft' | 'handler'>,
): string {
    const herkunft = normalizeLetzteAktionOriginLabel(row.herkunft);
    const handler = normalizeLetzteAktionOriginLabel(row.handler);
    if (row.aktion === TierLetzteAktionTyp.einstallen) {
        if (herkunft) {
            return herkunft;
        }
        if (handler) {
            return handler;
        }
        return '—';
    }
    return herkunft || '—';
}

export function isLetzteAktionHerkunftZielShowingHandler(
    row: Pick<TierLetzteAktionRow, 'aktion' | 'herkunft' | 'handler'>,
): boolean {
    if (row.aktion !== TierLetzteAktionTyp.einstallen) {
        return false;
    }
    return !normalizeLetzteAktionOriginLabel(row.herkunft) && !!normalizeLetzteAktionOriginLabel(row.handler);
}

export type LetzteAktionHerkunftZielKind = 'herkunft' | 'handler';

/** Which origin field is shown in Herkunft/Zielbetrieb for Einstallen rows; null when empty or Ausstallen. */
export function getLetzteAktionHerkunftZielKind(
    row: Pick<TierLetzteAktionRow, 'aktion' | 'herkunft' | 'handler'>,
): LetzteAktionHerkunftZielKind | null {
    if (row.aktion !== TierLetzteAktionTyp.einstallen) {
        return null;
    }
    if (getLetzteAktionHerkunftZielDisplay(row) === '—') {
        return null;
    }
    return isLetzteAktionHerkunftZielShowingHandler(row) ? 'handler' : 'herkunft';
}

export function getLetzteAktionHerkunftZielKindLabel(
    row: Pick<TierLetzteAktionRow, 'aktion' | 'herkunft' | 'handler'>,
): string | null {
    const kind = getLetzteAktionHerkunftZielKind(row);
    if (!kind) {
        return null;
    }
    return kind === 'handler' ? 'Händler' : 'Herkunft';
}

/** Used when merging lazy-loaded price/weight aggregates into a row. */
export function parseWertAnzahl(value: LetzteAktionWertAnzahl | null | undefined): LetzteAktionWertAnzahl | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const wert = value.wert != null ? Number(value.wert) : null;
    const anzahl = value.anzahl != null ? Number(value.anzahl) : null;
    if ((wert == null || Number.isNaN(wert)) && (anzahl == null || Number.isNaN(anzahl))) {
        return null;
    }
    return {
        wert: wert != null && !Number.isNaN(wert) ? wert : null,
        anzahl: anzahl != null && !Number.isNaN(anzahl) ? anzahl : null,
    };
}

/**
 * For Letzte-Aktionen metrics, {@link LetzteAktionWertAnzahl.wert} is the sum of all animal prices on the list.
 */
export function getLetzteAktionPreisGesamt(
    metric: LetzteAktionWertAnzahl | null | undefined,
): number | null {
    if (metric?.wert == null || Number.isNaN(Number(metric.wert))) {
        return null;
    }
    return Number(metric.wert);
}

/** Per-animal average derived from total sum and count. */
export function getLetzteAktionPreisProTier(
    metric: LetzteAktionWertAnzahl | null | undefined,
): number | null {
    const gesamt = getLetzteAktionPreisGesamt(metric);
    const anzahl = metric?.anzahl != null ? Number(metric.anzahl) : 0;
    if (gesamt == null || anzahl <= 0) {
        return null;
    }
    return Math.round((gesamt / anzahl) * 100) / 100;
}

export function formatLetzteAktionPreisGesamt(
    metric: LetzteAktionWertAnzahl | null | undefined,
): string {
    const gesamt = getLetzteAktionPreisGesamt(metric);
    if (gesamt == null) {
        return '—';
    }
    return `${gesamt.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} €`;
}

export function formatLetzteAktionPreisProTier(
    metric: LetzteAktionWertAnzahl | null | undefined,
): string {
    const pro = getLetzteAktionPreisProTier(metric);
    if (pro == null) {
        return '—';
    }
    return `${pro.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} € / Tier`;
}

/** {@link LetzteAktionWertAnzahl.wert} is total kg for the action's animals. */
export function getLetzteAktionGewichtGesamt(
    metric: LetzteAktionWertAnzahl | null | undefined,
): number | null {
    if (metric?.wert == null || Number.isNaN(Number(metric.wert))) {
        return null;
    }
    return Number(metric.wert);
}

export function getLetzteAktionGewichtProTier(
    metric: LetzteAktionWertAnzahl | null | undefined,
): number | null {
    const gesamt = getLetzteAktionGewichtGesamt(metric);
    const anzahl = metric?.anzahl != null ? Number(metric.anzahl) : 0;
    if (gesamt == null || anzahl <= 0) {
        return null;
    }
    return Math.round((gesamt / anzahl) * 100) / 100;
}

export function formatLetzteAktionGewichtGesamt(
    metric: LetzteAktionWertAnzahl | null | undefined,
): string {
    const gesamt = getLetzteAktionGewichtGesamt(metric);
    if (gesamt == null) {
        return '—';
    }
    return `${gesamt.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} kg`;
}

export function formatLetzteAktionGewichtProTier(
    metric: LetzteAktionWertAnzahl | null | undefined,
): string {
    const pro = getLetzteAktionGewichtProTier(metric);
    if (pro == null) {
        return '—';
    }
    return `${pro.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} kg / Tier`;
}

function parseOptionalInt(value: number | string | null | undefined): number | null {
    if (value == null || value === '') {
        return null;
    }
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
}

export function getTierLetzteAktionLabel(typ: TierLetzteAktionTyp): string {
    return TIER_LETZTE_AKTION_LABELS[typ] ?? typ;
}

export function getTierLetzteAktionDisplayLabel(row: {
    aktion: TierLetzteAktionTyp;
    umstallen?: number | boolean | null;
}): string {
    if (
        row.aktion === TierLetzteAktionTyp.ausstallen &&
        (KieneUtils.isApiOne(row.umstallen) || row.umstallen === true)
    ) {
        return 'Umstallen';
    }
    return getTierLetzteAktionLabel(row.aktion);
}

/** Tierliste status 162+ = abgeschlossen / importiert (not open for abschliessen). */
export function isTierlisteClosed(statusId: number | null | undefined): boolean {
    return statusId != null && statusId >= 162;
}

export function getStatusChipClass(statusId: number | null | undefined): string {
    return getListenstatusChipClass(statusId ?? null);
}

export function getListenstatusChipClass(statusId: number | null): string {
    if (statusId === 161) {
        return 'item-chip--status-161';
    }
    if (statusId === 162) {
        return 'item-chip--status-162';
    }
    if (statusId === 163) {
        return 'item-chip--status-163';
    }
    return '';
}

/** Tierlisten bereit zum Import / Umstall wartet auf Ziel-import — oben sortieren */
export function isLetzteAktionImportPrioritaet(row: TierLetzteAktionRow): boolean {
    if (row.aktion === TierLetzteAktionTyp.einstallen && row.tierliste_id && row.tierliste_id > 0) {
        const sid = row.status_id_anzeige ?? row.listenstatus_id;
        return KieneUtils.apiStatusId(sid, 162);
    }
    return row.aktion === TierLetzteAktionTyp.ausstallen && !!row.bevorzugt_importieren;
}

export const LETZTE_AKTION_PERMISSION_IMPORTIEREN = 9322;

/** Recipient declined import (Nicht importieren). */
export function isLetzteAktionRowImportClosed(row: TierLetzteAktionRow): boolean {
    if (isDirectScanEinstallTierliste(row)) {
        return false;
    }
    if (!isImportEinstallTierliste(row)) {
        return false;
    }
    return row.import_einstallen === false && !!row.tierliste_id;
}

/**
 * Pending Umstall/import recipient row (synthetic Einstallen row or Ausstall · bereit).
 * status_id_anzeige 162 on Einstallen = list ready to import into a target group.
 */
export function isPendingImportLetzteAktionRow(row: TierLetzteAktionRow): boolean {
    if (!row.tierliste_id || row.tierliste_id <= 0) {
        return false;
    }
    if (isLetzteAktionRowImportClosed(row)) {
        return false;
    }
    if (isDirectScanEinstallTierliste(row)) {
        return false;
    }
    if (isLetzteAktionImportPrioritaet(row)) {
        return true;
    }
    return row.aktion === TierLetzteAktionTyp.einstallen && getLetzteAktionenStatusPhase(row) === 'erstellt';
}

/** Same rules as detail dialog „In Gruppe importieren“ (row-level, no loaded tierliste). */
export function canShowLetzteAktionRowImportIcon(row: TierLetzteAktionRow): boolean {
    return isPendingImportLetzteAktionRow(row);
}

export type LetzteAktionPhase = 'erstellt' | 'abgeschlossen';

export function getLetzteAktionenStatusPhase(row: TierLetzteAktionRow): LetzteAktionPhase {
    const sid = row.status_id_anzeige ?? row.listenstatus_id;
    const statusNum = sid != null ? Number(sid) : 0;

    if (row.aktion === TierLetzteAktionTyp.einstallen) {
        if (!row.tierliste_id || row.tierliste_id <= 0) {
            return 'abgeschlossen';
        }
        if (statusNum >= 163) {
            return 'abgeschlossen';
        }
        return 'erstellt';
    }

    /** Ausstallen */
    if (!row.tierliste_id || row.tierliste_id <= 0) {
        return 'abgeschlossen';
    }
    if (row.bevorzugt_importieren) {
        return 'erstellt';
    }
    if (statusNum >= 162) {
        return 'abgeschlossen';
    }
    return 'erstellt';
}

/** Anzeige speziell „Letzte Aktionen“-Tabelle: nur Einstallen/Ausstallen + erstellt/abgeschlossen */
export function getLetzteAktionenDisplayedStatusLabel(row: TierLetzteAktionRow): string {
    const phase = getLetzteAktionenStatusPhase(row);
    const phaseLabel = phase === 'erstellt' ? 'Erstellt' : 'Abgeschlossen';
    return phaseLabel;
}

export function getLetzteAktionenDisplayedChipClass(row: TierLetzteAktionRow): string {
    return getLetzteAktionenStatusPhase(row) === 'erstellt' ? 'item-chip--la-erstellt' : 'item-chip--la-abgeschlossen';
}

/** Einstallen via tierliste row (import or direct scan). */
export function isImportedTierlisteEinstallen(row: TierLetzteAktionRow): boolean {
    return row.aktion === TierLetzteAktionTyp.einstallen && row.tierliste_id != null && row.tierliste_id > 0;
}

/**
 * Direct scan Einstallen: hidden typ=einstall tierliste; animals linked via gt.einstall_tierliste_id.
 */
export function isDirectScanEinstallTierliste(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null } | null,
): boolean {
    if (row.aktion !== TierLetzteAktionTyp.einstallen) {
        return false;
    }
    const tid = Number(row.tierliste_id) || 0;
    if (tid <= 0) {
        return false;
    }
    if ((row.tierliste_typ || tierliste?.typ || '').toLowerCase() === 'einstall') {
        return true;
    }
    const einstallTid = Number(row.einstall_tierliste_id) || 0;
    return einstallTid > 0 && einstallTid === tid;
}

/** Import-based Einstallen (Ausstall-tierliste → Zielgruppe), not direct scan. */
export function isImportEinstallTierliste(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null } | null,
): boolean {
    return isImportedTierlisteEinstallen(row) && !isDirectScanEinstallTierliste(row, tierliste);
}

export function canContinueDirectScanEinstall(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null } | null,
): boolean {
    return isDirectScanEinstallTierliste(row, tierliste) && getLetzteAktionenStatusPhase(row) === 'erstellt';
}

/** Tierlisten-Status aus Letzte-Aktionen-Zeile (161/162/163). */
export function getTierlisteStatusIdFromRow(row: TierLetzteAktionRow): number {
    return Number(row.status_id_anzeige ?? row.listenstatus_id ?? 0);
}

/**
 * Import-Einstallen abgeschlossen: status 163 + Tiere in Zielgruppe (gruppe_id_einstallen).
 * Erstellt / bereit zum Importieren (162) ist ausgeschlossen.
 */
export function isImportedEinstallAbgeschlossen(
    row: TierLetzteAktionRow,
    tierliste?: {
        typ?: string | null;
        status_id?: number | null;
        gruppe_id_einstallen?: number | null;
        tierliste_id?: number | null;
    } | null,
): boolean {
    if (!isImportedTierlisteEinstallen(row)) {
        return false;
    }
    const einstallTid = Number(row.einstall_tierliste_id) || 0;
    if (einstallTid > 0) {
        return Number(tierliste?.gruppe_id_einstallen) > 0 || (row.gruppe_id ?? 0) > 0;
    }
    if (tierliste?.typ === 'einstall') {
        return Number(tierliste.gruppe_id_einstallen) > 0;
    }
    const statusId =
        tierliste?.status_id != null && Number(tierliste.status_id) > 0
            ? Number(tierliste.status_id)
            : getTierlisteStatusIdFromRow(row);
    if (statusId < 163) {
        return false;
    }
    return Number(tierliste?.gruppe_id_einstallen) > 0;
}

/** Tierliste id to load for imported Einstallen detail (Einstall-liste, not Ausstall). */
export function getEinstallTierlisteIdForRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null } | null,
): number | null {
    const resolved = Number(row.einstall_tierliste_id) || 0;
    if (resolved > 0) {
        return resolved;
    }
    if (tierliste?.typ === 'einstall' && Number(tierliste.tierliste_id) > 0) {
        return Number(tierliste.tierliste_id);
    }
    return null;
}

/**
 * Import/Umstall: Tiere noch auf der Ausstall-Tierliste (gt.tierliste_id), nicht in Zielgruppe.
 * Erstellt in Letzte Aktionen (161) oder Bereit zum Importieren (162).
 */
export function isPendingImportEinstallRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): boolean {
    if (!isImportEinstallTierliste(row, tierliste)) {
        return false;
    }
    if (isImportedEinstallAbgeschlossen(row, tierliste)) {
        return false;
    }
    return getLetzteAktionenStatusPhase(row) === 'erstellt';
}

/** Gewicht/Preis erst nach Import in der Zielgruppe — vorher kein Metrik-Block. */
export function shouldShowGewichtPreisMetricsForLetzteAktionRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): boolean {
    return !isPendingImportEinstallRow(row, tierliste);
}

/** Herkunft kommt von der Ausstall-Tierliste (kunde_id_abgang) — vor Import nicht bearbeiten. */
export function canEditHerkunftForLetzteAktionRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): boolean {
    return !isPendingImportEinstallRow(row, tierliste);
}

export interface LetzteAktionHandlerTier {
    viehhaendler_name?: string | null;
}

/** Aggregated legacy Viehhändler label across animals. */
export function aggregateHandlerLabelFromTiere(tiere: readonly LetzteAktionHandlerTier[]): string {
    const names: string[] = [];
    const seen = new Set<string>();
    for (const tier of tiere) {
        const name = (tier.viehhaendler_name || '').trim();
        if (!name) {
            continue;
        }
        const key = name.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        names.push(name);
    }
    if (names.length === 0) {
        return '';
    }
    if (names.length === 1) {
        return names[0];
    }
    return `${names[0]} (+${names.length - 1} weitere)`;
}

/** Produktionsabschnitt der Zielgruppe — vor Import unbekannt. */
export function shouldShowAbschnittForLetzteAktionRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): boolean {
    return !isPendingImportEinstallRow(row, tierliste);
}

export function getLetzteAktionAbschnittDisplay(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): string {
    if (!shouldShowAbschnittForLetzteAktionRow(row, tierliste)) {
        return '—';
    }
    return (row.abschnitt || '—').trim() || '—';
}

/** Zielgruppe erst nach Import — in Liste/Detail nicht anzeigen. */
export function shouldShowAktuelleGruppeForRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): boolean {
    return !isPendingImportEinstallRow(row, tierliste);
}

export function getLetzteAktionAktuelleGruppeDisplay(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): string {
    if (!shouldShowAktuelleGruppeForRow(row, tierliste)) {
        return '—';
    }
    return row.aktuelle_gruppe || '—';
}

/** Tierliste id whose kunde_id_abgang is shown as Herkunft on the dashboard. */
export function resolveAusstallTierlisteIdForHerkunft(
    tierlisteId: number,
    tierliste?: { typ?: string | null; ausstall_tierliste_id?: number | null } | null,
): number {
    const tid = Number(tierlisteId) || 0;
    if (tid <= 0) {
        return 0;
    }
    const typ = (tierliste?.typ || '').toLowerCase();
    if (typ === 'einstall') {
        const parent = Number(tierliste?.ausstall_tierliste_id) || 0;
        return parent > 0 ? parent : tid;
    }
    return tid;
}

/** Ausstall-tierliste_id for animals before import (row may reference Ausstall- or Einstall-liste). */
export function getAusstallTierlisteIdForImportRow(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null; ausstall_tierliste_id?: number | null } | null,
): number | null {
    const tid = Number(row.tierliste_id) || 0;
    if (tid <= 0) {
        return null;
    }
    const typ = (row.tierliste_typ || tierliste?.typ || '').toLowerCase();
    if (typ === 'einstall') {
        const parent = Number(tierliste?.ausstall_tierliste_id) || 0;
        return parent > 0 ? parent : null;
    }
    return tid;
}

/** Import-tierliste: Gewicht/Preis nur nach Import in Zielgruppe (status 163). */
export function canEditImportedEinstallGewichtPreis(
    row: TierLetzteAktionRow,
    tierliste?: { status_id?: number | null; gruppe_id_einstallen?: number | null } | null,
): boolean {
    return isImportedEinstallAbgeschlossen(row, tierliste);
}

/** Direct-scan Einstall: Gewicht/Preis in erstellt und nach Liste abschliessen (status 163). */
export function canEditDirectScanEinstallGewichtPreis(
    row: TierLetzteAktionRow,
    tierliste?: { typ?: string | null; tierliste_id?: number | null } | null,
): boolean {
    return isDirectScanEinstallTierliste(row, tierliste);
}

/** YYYY-MM-DD from Letzte Aktionen row (action day = filter for Tiere in group). */
export function getLetzteAktionEinstallDatumYmd(row: TierLetzteAktionRow): string | null {
    const raw = (row.datum_raw || '').trim();
    if (raw.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(0, 10);
    }
    if (!row.datum || Number.isNaN(row.datum.getTime())) {
        return null;
    }
    return formatDate(row.datum, 'yyyy-MM-dd', 'de', 'Europe/Berlin');
}

function parseLetzteAktionYmd(value?: string | null): string | null {
    const raw = (value || '').trim();
    if (raw.length < 10 || !/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return null;
    }
    return raw.slice(0, 10);
}

function formatLetzteAktionYmdDe(ymd: string): string {
    const [year, month, day] = ymd.split('-');
    return `${day}.${month}.${year}`;
}

/** Einstall- or Ausstalldatum of the animals: single day or from–to (11. - 14.08.2026). */
export function formatLetzteAktionBewegungsdatum(
    row: Pick<TierLetzteAktionRow, 'bewegungsdatum_von' | 'bewegungsdatum_bis'>,
): string {
    const von = parseLetzteAktionYmd(row.bewegungsdatum_von);
    const bis = parseLetzteAktionYmd(row.bewegungsdatum_bis) || von;
    const start = von || bis;
    if (!start) {
        return '—';
    }
    if (!bis || start === bis) {
        return formatLetzteAktionYmdDe(start);
    }
    const [, startMonth, startDay] = start.split('-');
    const [endYear, endMonth] = bis.split('-');
    const [startYear] = start.split('-');
    if (startYear === endYear && startMonth === endMonth) {
        return `${Number(startDay)}. - ${formatLetzteAktionYmdDe(bis)}`;
    }
    if (startYear === endYear) {
        return `${startDay}.${startMonth}. - ${formatLetzteAktionYmdDe(bis)}`;
    }
    return `${formatLetzteAktionYmdDe(start)} - ${formatLetzteAktionYmdDe(bis)}`;
}

/** Erlösdifferenz / Tageszunahmen need Abgangsdatum (Ausstallen abgeschlossen, tierliste status 162+). */
export function canShowAusstallenErloesTageszunahmen(row: TierLetzteAktionRow): boolean {
    if (row.aktion !== TierLetzteAktionTyp.ausstallen) {
        return false;
    }
    return getLetzteAktionenStatusPhase(row) === 'abgeschlossen';
}

function parseGruppenFromApi(item: TierLetzteAktionApiRow): TierLetzteAktionGruppeLink[] {
    const raw = item.gruppen;
    if (Array.isArray(raw)) {
        return raw
            .map(g => ({
                gruppe_id: Number(g?.gruppe_id),
                bezeichnung: String(g?.bezeichnung || '—').trim() || '—',
            }))
            .filter(g => !Number.isNaN(g.gruppe_id) && g.gruppe_id > 0);
    }
    if (typeof raw === 'string' && raw.trim() !== '') {
        return raw
            .split(',')
            .map(part => {
                const segments = part.split('|');
                if (segments.length < 2) {
                    return null;
                }
                const gruppe_id = Number(segments[0]);
                if (Number.isNaN(gruppe_id) || gruppe_id <= 0) {
                    return null;
                }
                return {
                    gruppe_id,
                    bezeichnung: segments.slice(1).join('|').trim() || '—',
                };
            })
            .filter((g): g is TierLetzteAktionGruppeLink => g !== null);
    }
    return [];
}

/** Links for the Aktuelle Gruppe column (comma-separated in the template). */
export function getGruppeLinksForRow(row: TierLetzteAktionRow): TierLetzteAktionGruppeLink[] {
    if (row.gruppen?.length) {
        return row.gruppen;
    }
    if (row.gruppe_id != null && row.gruppe_id > 0) {
        return [
            {
                gruppe_id: row.gruppe_id,
                bezeichnung: (row.aktuelle_gruppe || '—').trim() || '—',
            },
        ];
    }
    return [];
}

/** Direct-scan einstall tierlists (typ=einstall) never use import_einstallen. */
function mapLetzteAktionImportEinstallen(item: TierLetzteAktionApiRow): boolean | null {
    const typ = (item.tierliste_typ || '').trim().toLowerCase();
    if (typ === 'einstall') {
        return null;
    }
    if (item.import_einstallen == null) {
        return null;
    }
    return KieneUtils.isApiOne(item.import_einstallen) || item.import_einstallen === true;
}

export function mapLetzteAktionApiRow(item: TierLetzteAktionApiRow): TierLetzteAktionRow | null {
    if (!item?.aktion || !VALID_AKTION_TYPES.has(item.aktion)) {
        return null;
    }
    const gid = item.gruppe_id != null && item.gruppe_id !== '' ? Number(item.gruppe_id) : NaN;
    let gruppen = parseGruppenFromApi(item);
    if (gruppen.length === 0 && !Number.isNaN(gid) && gid > 0) {
        gruppen = [
            {
                gruppe_id: gid,
                bezeichnung: (item.aktuelle_gruppe || '—').trim() || '—',
            },
        ];
    }
    const singleGruppeId = gruppen.length === 1 ? gruppen[0].gruppe_id : !Number.isNaN(gid) && gid > 0 ? gid : null;
    const tidRaw = item.tierliste_id;
    const tid = tidRaw != null && tidRaw !== '' ? Number(tidRaw) : NaN;
    const tierliste_id = !Number.isNaN(tid) && tid > 0 ? tid : null;
    const status_id_anzeige = parseOptionalInt(
        item.status_id_anzeige ?? item.listenstatus_id ?? item.status_id,
    );
    const statusLabel = (item.status || item.listenstatus || '—').trim() || '—';

    return {
        aktion_id: parseOptionalInt(item.aktion_id),
        aktion: item.aktion as TierLetzteAktionTyp,
        betrieb: item.betrieb || '—',
        gruppe_id: singleGruppeId,
        gruppen,
        anzahl_tiere: Number(item.anzahl_tiere || 0),
        aktuelle_gruppe:
            gruppen.length > 1
                ? gruppen.map(g => g.bezeichnung).join(', ')
                : item.aktuelle_gruppe || gruppen[0]?.bezeichnung || '—',
        abschnitt: (item.abschnitt || '—').trim() || '—',
        status: statusLabel,
        status_id_anzeige,
        listenstatus: statusLabel,
        listenstatus_id: status_id_anzeige,
        tierliste_id,
        tierliste_typ: (item.tierliste_typ || '').trim() || null,
        einstall_tierliste_id: parseOptionalInt(item.einstall_tierliste_id),
        import_einstallen: mapLetzteAktionImportEinstallen(item),
        kommentar: (item.kommentar || '').trim(),
        herkunft: item.herkunft || '—',
        handler: (item.handler || '').trim() || null,
        transfer_innerhalb_betrieb:
            KieneUtils.isApiOne(item.transfer_innerhalb_betrieb) || item.transfer_innerhalb_betrieb === true,
        umstallen: KieneUtils.isApiOne(item.umstallen) || item.umstallen === true,
        bevorzugt_importieren: KieneUtils.isApiOne(item.bevorzugt_importieren) || item.bevorzugt_importieren === true,
        status_hinweis: (item.status_hinweis || '').trim() || null,
        datum_raw: (item.datum || '').trim() || null,
        datum: item.datum ? new Date(item.datum) : new Date(),
        bewegungsdatum_von: parseLetzteAktionYmd(item.bewegungsdatum_von),
        bewegungsdatum_bis: parseLetzteAktionYmd(item.bewegungsdatum_bis),
        kunde_id: parseOptionalInt(item.kunde_id),
        darf_bio: item.darf_bio == null || item.darf_bio === '' ? null : Number(item.darf_bio) > 0 ? 1 : 0,
        einkaufspreis_avg: parseWertAnzahl(item.einkaufspreis_avg),
        verkaufspreis_avg: parseWertAnzahl(item.verkaufspreis_avg),
        erloesdifferenz_avg: parseWertAnzahl(item.erloesdifferenz_avg),
        tageszunahmen_avg: parseWertAnzahl(item.tageszunahmen_avg),
        einstallgewicht_avg: parseWertAnzahl(item.einstallgewicht_avg),
        abgangsgewicht_avg: parseWertAnzahl(item.abgangsgewicht_avg),
    };
}

export interface TierlisteGruppenKontextGruppe {
    gruppe_id: number;
    bezeichnung: string;
    abschnitt?: string;
}

export interface TierlisteGruppenKontext {
    tierliste_id?: number;
    gruppen: TierlisteGruppenKontextGruppe[];
    abschnitt: string;
}

export function isGlobalAusstallTierliste(
    tierliste?: { mehrere_gruppen?: number | null } | null,
    row?: Pick<TierLetzteAktionRow, 'aktuelle_gruppe'> | null,
): boolean {
    if (KieneUtils.isApiOne(tierliste?.mehrere_gruppen)) {
        return true;
    }
    return (row?.aktuelle_gruppe || '').trim().toLowerCase() === 'global';
}

/** Synthetic Letzte-Aktionen row for /gruppen/tierlisten (open Ausstall-tierliste in dashboard detail dialog). */
export function mapMeineTierlisteToLetzteAktionRow(tierliste: {
    tierliste_id?: number | null;
    mehrere_gruppen?: number | null;
    gruppe_id_abgang?: number | null;
    gruppe_abgang?: string | null;
    kunden_abgang?: string | null;
    kunde_abgang?: string | null;
    anzahl_tiere?: number | null;
    status?: string | null;
    status_id?: number | null;
    import_einstallen?: number | null;
    transfer_innerhalb_betrieb?: number | boolean | null;
    umstallen?: number | boolean | null;
    kommentar?: string | null;
    datum_abgang?: Date | string | null;
    erstellt?: Date | string | null;
}): TierLetzteAktionRow {
    const isGlobal = KieneUtils.isApiOne(tierliste.mehrere_gruppen);
    const gruppeId = Number(tierliste.gruppe_id_abgang) || 0;
    const gruppen: TierLetzteAktionGruppeLink[] = [];
    if (!isGlobal && gruppeId > 0) {
        gruppen.push({
            gruppe_id: gruppeId,
            bezeichnung: (tierliste.gruppe_abgang || '—').trim() || '—',
        });
    }

    const datumSource = tierliste.datum_abgang || tierliste.erstellt;
    let datumRaw: string | null = null;
    let datum = new Date();
    if (datumSource) {
        const parsed = datumSource instanceof Date ? datumSource : new Date(datumSource);
        if (!Number.isNaN(parsed.getTime())) {
            datum = parsed;
            datumRaw = formatDate(parsed, 'yyyy-MM-dd HH:mm:ss', 'de');
        }
    }

    const statusLabel = (tierliste.status || '—').trim() || '—';
    const statusId = tierliste.status_id != null ? Number(tierliste.status_id) : null;

    return {
        aktion_id: null,
        aktion: TierLetzteAktionTyp.ausstallen,
        betrieb: (tierliste.kunden_abgang || tierliste.kunde_abgang || '—').trim() || '—',
        gruppe_id: !isGlobal && gruppen.length === 1 ? gruppen[0].gruppe_id : null,
        gruppen,
        anzahl_tiere: Math.max(0, Number(tierliste.anzahl_tiere) || 0),
        aktuelle_gruppe: isGlobal ? 'Global' : (tierliste.gruppe_abgang || '—').trim() || '—',
        abschnitt: '—',
        status: statusLabel,
        status_id_anzeige: statusId,
        listenstatus: statusLabel,
        listenstatus_id: statusId,
        tierliste_id: Number(tierliste.tierliste_id) > 0 ? Number(tierliste.tierliste_id) : null,
        import_einstallen:
            tierliste.import_einstallen == null ? null : KieneUtils.isApiOne(tierliste.import_einstallen),
        kommentar: (tierliste.kommentar || '').trim(),
        herkunft: '—',
        umstallen: KieneUtils.isApiOne(tierliste.umstallen) || tierliste.umstallen === true,
        transfer_innerhalb_betrieb: KieneUtils.isApiOne(tierliste.umstallen) || tierliste.umstallen === true,
        bevorzugt_importieren: false,
        status_hinweis: null,
        datum_raw: datumRaw,
        datum,
        bewegungsdatum_von: parseLetzteAktionYmd(
            datumSource instanceof Date
                ? formatDate(datumSource, 'yyyy-MM-dd', 'de')
                : typeof datumSource === 'string'
                    ? datumSource
                    : null,
        ),
        bewegungsdatum_bis: parseLetzteAktionYmd(
            datumSource instanceof Date
                ? formatDate(datumSource, 'yyyy-MM-dd', 'de')
                : typeof datumSource === 'string'
                    ? datumSource
                    : null,
        ),
        einkaufspreis_avg: null,
        verkaufspreis_avg: null,
        erloesdifferenz_avg: null,
        tageszunahmen_avg: null,
        einstallgewicht_avg: null,
        abgangsgewicht_avg: null,
    };
}

export function applyTierlisteGruppenKontextToLetzteAktionRow(
    row: TierLetzteAktionRow,
    kontext: TierlisteGruppenKontext,
    isGlobal: boolean,
): TierLetzteAktionRow {
    const kontextGruppen = kontext.gruppen || [];
    const abschnitt = (kontext.abschnitt || '—').trim() || '—';
    const firstGruppeId = kontextGruppen.length > 0 ? kontextGruppen[0].gruppe_id : row.gruppe_id;

    if (isGlobal) {
        return {
            ...row,
            gruppen: [],
            gruppe_id: firstGruppeId,
            aktuelle_gruppe: 'Global',
            abschnitt,
        };
    }

    const gruppen: TierLetzteAktionGruppeLink[] = kontextGruppen.map(g => ({
        gruppe_id: g.gruppe_id,
        bezeichnung: (g.bezeichnung || '—').trim() || '—',
    }));
    if (gruppen.length === 0) {
        return { ...row, abschnitt };
    }

    return {
        ...row,
        gruppen,
        gruppe_id: gruppen.length === 1 ? gruppen[0].gruppe_id : null,
        aktuelle_gruppe:
            gruppen.length === 1 ? gruppen[0].bezeichnung : gruppen.map(g => g.bezeichnung).join(', '),
        abschnitt,
    };
}
